import type { SortDirection } from "./dropTableSort";

/*
 * The sort and search of a customer's Shopify tab. Both are a view over the rows already loaded
 * (`shopify.userPurchases`, at most 200), so neither refetches.
 */

export const SHOPIFY_PURCHASE_SORT_KEYS = ["paid", "status", "bought"] as const;
export type ShopifyPurchaseSortKey = (typeof SHOPIFY_PURCHASE_SORT_KEYS)[number];
export interface ShopifyPurchaseSort {
  key: ShopifyPurchaseSortKey;
  direction: SortDirection;
}

/** Newest first, the order the server returns. */
export const DEFAULT_SHOPIFY_PURCHASE_SORT: ShopifyPurchaseSort = {
  key: "bought",
  direction: "desc",
};

/** A purchase's lifecycle, so a status sort reads as progress rather than the alphabet. */
const STATUS_ORDER = [
  "processing",
  "unrevealed",
  "converted",
  "revealed",
  "claimed",
  "sold",
  "voided",
];

export interface ShopifyPurchaseRow {
  orderName: string;
  orderGid: string;
  paidPriceCents: number;
  status: string;
  createdAt: string | Date;
}

/** A header click: a new column opens descending, a second click flips it. */
export function nextShopifyPurchaseSort(
  current: ShopifyPurchaseSort,
  key: ShopifyPurchaseSortKey,
): ShopifyPurchaseSort {
  if (current.key !== key) return { key, direction: key === "status" ? "asc" : "desc" };
  return { key, direction: current.direction === "asc" ? "desc" : "asc" };
}

function sortValue(row: ShopifyPurchaseRow, key: ShopifyPurchaseSortKey): number {
  if (key === "paid") return row.paidPriceCents;
  if (key === "bought") return new Date(row.createdAt).getTime();
  const index = STATUS_ORDER.indexOf(row.status);
  return index === -1 ? STATUS_ORDER.length : index;
}

/**
 * Whether the row's order matches the search: its name ("#1042", with or without the "#") or
 * the numeric id at the end of its gid, the number in the order's admin URL.
 */
export function matchesOrderSearch(row: ShopifyPurchaseRow, search: string): boolean {
  const term = search.trim().toLowerCase().replace(/^#/, "");
  if (term === "") return true;
  const orderId = row.orderGid.split("/").pop() ?? "";
  return row.orderName.toLowerCase().replace(/^#/, "").includes(term) || orderId.includes(term);
}

/** The rows to render, filtered by order and sorted. Ties keep the server's order. */
export function viewShopifyPurchases<T extends ShopifyPurchaseRow>(
  rows: readonly T[],
  sort: ShopifyPurchaseSort,
  search: string,
): T[] {
  const sign = sort.direction === "asc" ? 1 : -1;
  return rows
    .filter((row) => matchesOrderSearch(row, search))
    .map((row, index) => ({ row, index, value: sortValue(row, sort.key) }))
    .sort((a, b) => (a.value - b.value) * sign || a.index - b.index)
    .map(({ row }) => row);
}
